import { ReactElement, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import {
  colorBgWithStateAutoFont,
  colorBorder,
  colorFillColor
} from 'mt-react-theme-manager';

import Modal from '..';
import { Preset } from '../../components/Button';
import { TextButton } from '../../components/TextButton';
import ContentAreaFrame from '../../components/ContentAreaFrame';
import EmailInput, { emailValidation } from '../../components/EmailInput';
import { qsConfigs } from '../../lib/helpers';

import './style.scss';

interface Props {
  email?: string;
  onComplete: (email: string) => void;
}

function loginHereUrl(email: string) {
  const configs = qsConfigs(true);
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  // forward email to login UI if it is valid or else keep it empty
  if (emailValidation(email)) {
    configs.email = email;
  } else {
    delete configs.email;
  }

  params.configs = qs.stringify(configs, { encode: true });

  return `oauth/authorize${qs.stringify(params, { addQueryPrefix: true })}`;
}

export default function InvalidEmailModal({
  email = '',
  onComplete
}: Props): ReactElement {
  const { t } = useTranslation();
  const [{ newEmail, isEmailValid }, setEmailInfo] = useState({
    newEmail: '',
    isEmailValid: false
  });

  const emailInputId = 'invalid-email-input';

  const onChangeEmail = useCallback(
    (newEmail: string, newEmailIsValid: boolean) => {
      setEmailInfo({
        newEmail,
        isEmailValid: newEmailIsValid
      });
    },
    []
  );

  const changeEmail = useCallback(
    () => onComplete(newEmail),
    [onComplete, newEmail]
  );

  // NOTE:
  // treat it as invalid when it contains string that is not a RFC spec compliance email,
  // empty means the email is not there at all,
  // when is it an invalid email case, we will display the invalid string
  const invalidEmail = email.trim().length > 0;

  return (
    <Modal>
      <form onSubmit={changeEmail}>
        <ContentAreaFrame className="invalid-email-modal">
          <div className="title">{t('InvalidEmailModal.heading')}</div>
          <label className="description" htmlFor={emailInputId}>
            {invalidEmail
              ? t('InvalidEmailModal.invalidEmailDescription')
              : t('InvalidEmailModal.emptyEmailDescription')}
          </label>
          {invalidEmail && (
            <div className="invalid-email-display">{email.trim()}</div>
          )}
          <EmailInput
            className={`email-input ${colorBorder('primary', 'base')} ${colorFillColor('primary', 'base')}`}
            onChangeText={onChangeEmail}
            id={emailInputId}
          />
          <a href={loginHereUrl(newEmail)} className="login-here">
            {t('InvalidEmailModal.loginHere')}
          </a>
          <TextButton
            big
            preset={Preset.None}
            className={colorBgWithStateAutoFont('primary')}
            text={t('InvalidEmailModal.ok')}
            type="submit"
            disabled={!isEmailValid}
          />
        </ContentAreaFrame>
      </form>
    </Modal>
  );
}
